import {
    FormControl,
    FormHelperText,
    Input,
    InputAdornment,
    InputLabel,
    MenuItem,
    MenuList,
    Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Downshift from "downshift";
import PropTypes from "prop-types";
import { useState } from "react";
import { emptyValues } from "../../../app/js/utils/constants";
import ErrorIcon from "./ErrorIcon";

const StyledSuggestionsContainer = styled("div")(() => ({
    flexGrow: 1,
    position: "relative",
}));

const StyledInput = styled(Input)(({ theme }) => ({
    borderRadius: 4,
    border: "1px solid #E0E0E0",
    fontSize: 14,
    padding: "4px",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    "&:focus": {
        borderColor: "#80bdff",
        boxShadow: "0 0 0 0.2rem rgba(0,123,255,.25)",
    },
}));

const StyledFormControl = styled(FormControl)(() => ({
    marginBottom: "20px",
    width: "100%",
}));

// When reserving label/helper space, the reserved helper line supplies the row's
// vertical spacing, so the FormControl drops its own bottom margin. This keeps the
// reserve behavior self-contained and leaves extraFormControlStyles as a clean
// escape hatch layered on top so extraFormControlStyles and reserveLabelSpace do not silently fight over margin.
const StyledReservedFormControl = styled(StyledFormControl)({
    marginBottom: 0,
});

const StyledPaper = styled(Paper)(() => ({
    position: "absolute",
    zIndex: 3,
    left: 0,
    right: 0,
    top: "61px",
    height: "210px",
    overflowY: "auto",
}));

const StyledMenuItemTitle = styled("span")({
    fontSize: "14px",
    margin: 0,
    padding: 0,
});

const StyledMenuItemSubtext = styled("span")({
    fontSize: "12px",
    margin: 0,
    padding: 0,
});

const StyledMenuItem = styled("div")({
    width: "100%",
    lineHeight: "1em",
});

const StyledResultType = styled("div")({
    float: "right",
    textTransform: "uppercase",
    fontSize: "13px",
    fontWeight: 600,
    color: "#9E9E9E",
});

const StyledInputLabel = styled(InputLabel)({
    fontSize: "13px",
    textTransform: "uppercase",
});

const StyledFormHelperText = styled(FormHelperText)(({ required }) => ({
    fontStyle: "italic",
    margin: "0px",

    ...(required && {
        color: "dimgrey",
    }),
}));

const StyledReservedInputLabel = styled(StyledInputLabel)({
    minHeight: "0.5em",
});

const StyledReservedHelperText = styled(StyledFormHelperText)({
    minHeight: "0.5em",
});

function renderInput(inputProps) {
    const {
        endAdornment,
        error,
        extraFormControlStyles,
        InputProps,
        label,
        ref,
        reserveLabelSpace,
        startAdornment,
        errorText,
        helperText,
        helperTextRequired,
        showHelperOnBlur,
    } = inputProps;

    const showHelperText = () => {
        if (helperText && !error && showHelperOnBlur) {
            return helperText;
        }
        return;
    };

    const helperTextContext = showHelperText();

    const FormControlComponent = reserveLabelSpace ? StyledReservedFormControl : StyledFormControl;
    const LabelComponent = reserveLabelSpace ? StyledReservedInputLabel : StyledInputLabel;
    // With reserveLabelSpace the label slot always renders to hold its height.
    const showLabelSlot = reserveLabelSpace || label || error;

    const HelperTextComponent = reserveLabelSpace ? StyledReservedHelperText : StyledFormHelperText;
    const renderHelperText = () => {
        if (errorText && error) {
            return <HelperTextComponent error={true}>{errorText}</HelperTextComponent>;
        }
        if (helperTextContext) {
            return <HelperTextComponent required={helperTextRequired}>{helperText}</HelperTextComponent>;
        }
        if (reserveLabelSpace) {
            return <StyledReservedHelperText>&nbsp;</StyledReservedHelperText>;
        }
        return null;
    };

    return (
        <FormControlComponent style={{ ...extraFormControlStyles }}>
            {showLabelSlot && (
                <LabelComponent shrink>
                    {label}
                    {error && <ErrorIcon />}
                </LabelComponent>
            )}
            <StyledInput
                inputRef={ref}
                disableUnderline={true}
                autoComplete={"no"}
                style={error && { borderColor: "red" }}
                endAdornment={endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>}
                startAdornment={startAdornment && <InputAdornment position="start">{startAdornment}</InputAdornment>}
                {...InputProps}
            />
            {renderHelperText()}
        </FormControlComponent>
    );
}

function renderSuggestion({
    suggestion,
    index,
    itemProps,
    highlightedIndex,
    selectedItem,
    key,
    display,
    displaySub,
    displayType,
}) {
    const isHighlighted = highlightedIndex === index;
    const isSelected = selectedItem ? (String(selectedItem[key]) || "").indexOf(suggestion[key]) > -1 : false;

    return (
        <MenuItem
            {...itemProps}
            key={key}
            selected={isHighlighted}
            component="div"
            className="waterloo"
            style={{
                fontWeight: isSelected ? 500 : 400,
            }}
        >
            <StyledMenuItem>
                <StyledMenuItemTitle>{display(suggestion)}</StyledMenuItemTitle>
                <br />
                <StyledMenuItemSubtext>{displaySub && displaySub(suggestion)}</StyledMenuItemSubtext>
            </StyledMenuItem>
            <StyledResultType>{displayType && <div>{displayType(suggestion)} Display Type</div>}</StyledResultType>
        </MenuItem>
    );
}

renderSuggestion.propTypes = {
    highlightedIndex: PropTypes.number,
    index: PropTypes.number,
    itemProps: PropTypes.object,
    selectedItem: PropTypes.string,
    suggestion: PropTypes.shape({ label: PropTypes.string }).isRequired,
};

const Autocomplete = (props) => {
    const {
        id,
        error,
        label,
        options,
        disabled,
        placeholder,
        suggestions,
        itemToString,
        endAdornment,
        freeFormValue,
        reserveLabelSpace,
        startAdornment,
        initialInputValue,
        errorText,
        extraFormControlStyles,
        helperText,
        searchFunction,
        freeformUpdate,
        onSelect,
        onBlurValidate,
        helperTextRequired,
    } = props;
    const [itemSelected, setItemSelected] = useState(true);
    const [inputValFromState, setInputValFromState] = useState(null);
    const [focused, setFocused] = useState(false);
    const isEmpty = (val) => emptyValues.has(val);
    // Whether the helper hint should be shown, re-evaluated on blur.
    // Initialized from the current value so a pre-populated field hides the hint on load.
    const [showHelperOnBlur, setShowHelperOnBlur] = useState(isEmpty(initialInputValue));

    const handleInputValueChange = (value) => {
        if (focused) {
            setItemSelected(false);
            setInputValFromState(value);
            searchFunction(value);
            freeformUpdate(value);
        }
    };

    const handleSelect = (item) => {
        setItemSelected(true);
        setInputValFromState(null);
        setShowHelperOnBlur(isEmpty(itemToString(item)));
        onSelect(item);
        if (onBlurValidate) {
            onBlurValidate(id, itemToString(item));
        }
    };

    const handleBlur = (prevVal) => {
        setItemSelected(false);
        setInputValFromState(prevVal);
        setFocused(false);
        setShowHelperOnBlur(isEmpty(prevVal));
        if (onBlurValidate) {
            onBlurValidate(id, prevVal);
        }
    };

    const handleFocus = () => {
        setFocused(true);
    };

    return (
        <div>
            <Downshift
                id={id}
                onSelect={handleSelect}
                itemToString={itemToString}
                onInputValueChange={handleInputValueChange}
                initialInputValue={initialInputValue}
            >
                {({
                    isOpen,
                    inputValue,
                    getItemProps,
                    getMenuProps,
                    selectedItem,
                    getInputProps,
                    getRootProps,
                    highlightedIndex,
                }) => {
                    return (
                        <StyledSuggestionsContainer {...getRootProps({}, { suppressRefError: true })}>
                            {renderInput({
                                fullWidth: true,
                                endAdornment: endAdornment,
                                startAdornment: startAdornment,
                                errorText: errorText,
                                extraFormControlStyles: extraFormControlStyles,
                                helperText: helperText,
                                helperTextRequired: helperTextRequired,
                                id: id,
                                label: label,
                                reserveLabelSpace: reserveLabelSpace,
                                error: error,
                                showHelperOnBlur: showHelperOnBlur,
                                InputProps: getInputProps({
                                    placeholder: placeholder,
                                    value: itemSelected ? initialInputValue : inputValFromState,
                                    disabled: disabled,
                                    onBlur: () => handleBlur(inputValFromState),
                                    onFocus: () => handleFocus(),
                                }),
                            })}
                            <div {...getMenuProps()}>
                                {isOpen && suggestions.length > 0 ? (
                                    <StyledPaper square>
                                        <MenuList>
                                            {suggestions &&
                                                suggestions.map((suggestion, index) =>
                                                    renderSuggestion({
                                                        suggestion,
                                                        index,
                                                        itemProps: getItemProps({ item: suggestion }),
                                                        highlightedIndex,
                                                        selectedItem: !freeFormValue ? null : selectedItem,
                                                        key: suggestion[options.key],
                                                        display: options.display,
                                                        displaySub: options.displaySub,
                                                        displayType: options.displayType,
                                                    })
                                                )}
                                        </MenuList>
                                    </StyledPaper>
                                ) : null}
                            </div>
                        </StyledSuggestionsContainer>
                    );
                }}
            </Downshift>
        </div>
    );
};

export default Autocomplete;